// ===================================================================
// KAIXO - Pestaña Cobros
// ===================================================================
// Muestra MRR/ARR, suscripciones activas, churn, conversión y la
// lista de pagos recientes. Pestañas internas: Resumen / Suscripciones
// / Pagos.
// ===================================================================

import React, { useEffect, useState } from 'react';
import {
    adminRevenueSummary,
    adminRevenueSubscriptions,
    adminRevenuePayments
} from '../../../services/adminApi';
import './AdminRevenueSection.css';

const TABS = [
    { id: 'summary',   label: 'Resumen' },
    { id: 'subs',      label: 'Suscripciones' },
    { id: 'payments',  label: 'Pagos' }
];

export default function AdminRevenueSection() {
    const [tab, setTab] = useState('summary');

    return (
        <div className="arv-wrap">
            <div className="arv-tabs">
                {TABS.map(t => (
                    <button
                        key={t.id}
                        className={`arv-tab ${tab === t.id ? 'is-active' : ''}`}
                        onClick={() => setTab(t.id)}
                    >{t.label}</button>
                ))}
            </div>
            {tab === 'summary'  && <SummaryView />}
            {tab === 'subs'     && <SubsView />}
            {tab === 'payments' && <PaymentsView />}
        </div>
    );
}

function fmtMoney(c, currency = 'USD') {
    return ((c || 0) / 100).toLocaleString('en-US', { style: 'currency', currency });
}

function SummaryView() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        adminRevenueSummary()
            .then(({ data }) => setData(data?.summary || null))
            .catch(err => setError(err.response?.data?.error || err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="ap-loading">Calculando…</div>;
    if (error) return <div className="ap-error">{error}</div>;
    if (!data) return null;

    // Backend devuelve mrr/arr ya en USD (no en centavos). Usamos Intl para
    // formatear igual que las demás secciones (símbolo + separadores locales).
    const fmtUsd = (n) => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    const cards = [
        { label: 'MRR', value: fmtUsd(data.mrr), sub: 'Ingreso recurrente mensual' },
        { label: 'ARR', value: fmtUsd(data.arr), sub: 'Anualizado' },
        { label: 'Suscripciones activas', value: data.activeSubscriptions, sub: '' },
        { label: 'Nuevas (30d)', value: data.new30d, sub: 'Crecimiento mensual' },
        { label: 'Cancelaciones (30d)', value: data.churned30d, sub: '' },
        { label: 'Tasa churn', value: data.churnRate + '%', sub: 'Mensual' },
        { label: 'Conversión free→pago', value: data.conversionPct + '%', sub: '' }
    ];

    return (
        <div className="arv-summary">
            <div className="arv-cards">
                {cards.map(c => (
                    <div className="arv-card" key={c.label}>
                        <div className="arv-card-label">{c.label}</div>
                        <div className="arv-card-value">{c.value}</div>
                        {c.sub && <div className="arv-card-sub">{c.sub}</div>}
                    </div>
                ))}
            </div>

            <div className="arv-by-plan">
                <h4>Suscripciones activas por plan</h4>
                {Object.keys(data.byPlan || {}).length === 0
                    ? <p className="arv-empty">No hay suscripciones activas todavía.</p>
                    : (
                        <table className="arv-table">
                            <thead><tr><th>Plan</th><th>Activos</th></tr></thead>
                            <tbody>
                                {Object.entries(data.byPlan).map(([plan, count]) => (
                                    <tr key={plan}><td>{plan}</td><td>{count}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    )
                }
            </div>
        </div>
    );
}

function SubsView() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        setLoading(true);
        adminRevenueSubscriptions(statusFilter ? { status: statusFilter } : {})
            .then(({ data }) => setList(data?.subscriptions || []))
            .catch(err => setError(err.response?.data?.error || err.message))
            .finally(() => setLoading(false));
    }, [statusFilter]);

    return (
        <div>
            <div className="arv-filter-row">
                <select className="arv-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="">Todos los estados</option>
                    <option value="active">Activas</option>
                    <option value="trial">Trial</option>
                    <option value="canceled">Canceladas</option>
                    <option value="expired">Expiradas</option>
                    <option value="past_due">Past due</option>
                </select>
            </div>

            {loading && <div className="ap-loading">Cargando…</div>}
            {error && <div className="ap-error">{error}</div>}
            {!loading && (
                <table className="arv-table">
                    <thead>
                        <tr>
                            <th>Usuario</th>
                            <th>Plan</th>
                            <th>Precio</th>
                            <th>Estado</th>
                            <th>Inicio</th>
                            <th>Fin</th>
                            <th>Provider</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map(s => (
                            <tr key={s._id}>
                                <td>{s.user?.name || '—'}</td>
                                <td>{s.plan}</td>
                                <td>{fmtMoney(s.priceCents, s.currency)}/{s.billingCycle === 'yearly' ? 'año' : 'mes'}</td>
                                <td><span className={`arv-status arv-status-${s.status}`}>{s.status}</span></td>
                                <td>{s.startDate ? new Date(s.startDate).toLocaleDateString() : '—'}</td>
                                <td>{s.endDate ? new Date(s.endDate).toLocaleDateString() : '—'}</td>
                                <td>{s.paymentProvider || '—'}</td>
                            </tr>
                        ))}
                        {list.length === 0 && (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                                Sin suscripciones.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function PaymentsView() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        adminRevenuePayments()
            .then(({ data }) => setList(data?.payments || []))
            .catch(err => setError(err.response?.data?.error || err.message))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div>
            {loading && <div className="ap-loading">Cargando pagos…</div>}
            {error && <div className="ap-error">{error}</div>}
            {!loading && (
                <table className="arv-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Usuario</th>
                            <th>Plan</th>
                            <th>Importe</th>
                            <th>Estado</th>
                            <th>Provider</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((p, i) => (
                            <tr key={i}>
                                <td>{new Date(p.date).toLocaleDateString()}</td>
                                <td>{p.user?.name || '—'}</td>
                                <td>{p.plan}</td>
                                <td>{fmtMoney(p.amountCents, p.currency)}</td>
                                <td>{p.status || '—'}</td>
                                <td>{p.paymentProvider || '—'}</td>
                            </tr>
                        ))}
                        {list.length === 0 && (
                            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                                Sin pagos registrados todavía.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}
